/**
 * Every eval run, newest first, one page at a time. The Evals page shows only the newest.
 */
import { ArrowLeft, RefreshCw } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Button from '../../components/Button';
import ErrorDisplay from '../../components/ErrorDisplay';
import LoadingSpinner from '../../components/LoadingSpinner';
import { RunList } from './components/RunList';
import { evalsService } from './services/evalsService';
import type { EvalRunItem, ListEvalSuitesResponse } from './types/evalTypes';

const PAGE_SIZE = 25;

export const EvalRuns = () => {
  const [runs, setRuns] = useState<EvalRunItem[]>([]);
  const [catalog, setCatalog] = useState<ListEvalSuitesResponse | null>(null);
  const [nextBefore, setNextBefore] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [page, suites] = await Promise.all([evalsService.listRuns(PAGE_SIZE), evalsService.listSuites()]);
      setRuns(page.runs);
      setNextBefore(page.nextBefore);
      setCatalog(suites);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Failed to load the runs.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const loadMore = async () => {
    if (nextBefore === null) return;
    setLoadingMore(true);
    setError(null);
    try {
      const page = await evalsService.listRuns(PAGE_SIZE, nextBefore);
      setRuns((current) => [...current, ...page.runs]);
      setNextBefore(page.nextBefore);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Failed to load more runs.');
    } finally {
      setLoadingMore(false);
    }
  };

  if (loading) return <LoadingSpinner message="Loading runs…" />;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto p-6 space-y-6">
        <div className="flex items-center justify-between gap-3">
          <Link to="/evals" className="inline-flex items-center gap-1 text-sm text-blue-600 hover:underline">
            <ArrowLeft size={14} /> Evals
          </Link>
          <Button variant="outline" size="sm" icon={<RefreshCw size={13} />} onClick={() => void load()}>
            Refresh
          </Button>
        </div>
        <h1 className="text-3xl font-bold text-gray-900">All runs</h1>

        {error && <ErrorDisplay message={error} />}

        <RunList runs={runs} models={catalog?.models ?? []} suites={catalog?.suites ?? []} />

        {nextBefore !== null && (
          <div className="flex justify-center">
            <Button variant="outline" size="sm" onClick={() => void loadMore()} disabled={loadingMore} loading={loadingMore}>
              Load more
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};
